import { BaseService } from '@/modules/Core/services/BaseService'
import type { IConfContratosxservicios } from '@/modules/Contratacion/interfaces'
import type { IResponseApi } from '@/modules/Core/interfaces'
import type { AxiosResponse } from 'axios'
import type {
  ICabeceraPortafolio,
  IGeneralPortafolio,
  IGuardadoPortafolio,
  IRegistroPortafolio,
  ResumenPortafolio
} from '@/modules/Contratacion/interfaces/IPortafolio'
import type { PortafolioItemType } from '@/modules/Contratacion/interfaces/tablas/IConfContratosxservicios'
export class ContratoxservicioService extends BaseService<IConfContratosxservicios> {
  constructor() {
    super({
      baseUrl: 'system/contracts-services/'
    })
  }
  async createMulti(
    data: IConfContratosxservicios[]
  ): Promise<AxiosResponse<IResponseApi<IConfContratosxservicios[]>>> {
    return this.http
      .post<IConfContratosxservicios[]>(`/create-multi`, data)
      .catch(this.errorHandle)
  }
  async updateMulti(
    data: IConfContratosxservicios[]
  ): Promise<AxiosResponse<IResponseApi<IConfContratosxservicios[]>>> {
    return this.http
      .put<IConfContratosxservicios[]>(`/update-multi`, data)
      .catch(this.errorHandle)
  }
  async deleteIds(
    ids: number[] | string[]
  ): Promise<AxiosResponse<IResponseApi<void>>> {
    return this.http.post<void>(`/deleteids`, ids).catch(this.errorHandle)
  }
  async getContract(
    id: number,
    params: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApi<IConfContratosxservicios[]>>> {
    return this.http
      .get<IConfContratosxservicios[]>(`/contract/${id}`, { params })
      .catch(this.errorHandle)
  }
  async headerPortafolio(
    cocoId: number
  ): Promise<AxiosResponse<IResponseApi<ICabeceraPortafolio>>> {
    try {
      const respuesta = await this.http.get<IResponseApi<ICabeceraPortafolio>>(
        `/portafolio/${cocoId}/header`
      )
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async generalPortafolio(
    cocoId: number,
    tipo: PortafolioItemType = 'CONSULTA'
  ): Promise<AxiosResponse<IResponseApi<IGeneralPortafolio>>> {
    try {
      const respuesta = await this.http.get<IResponseApi<IGeneralPortafolio>>(
        `/portafolio/${cocoId}/general`,
        { params: { tipo } }
      )
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async exportPortafolio(
    cocoId: number
  ): Promise<AxiosResponse<IResponseApi<IRegistroPortafolio[]>>> {
    try {
      const respuesta = await this.http.get<
        IResponseApi<IRegistroPortafolio[]>
      >(`/portafolio/${cocoId}/export`)
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async searchPortafolio(
    cocoId: number,
    params: Record<string, unknown>
  ): Promise<
    AxiosResponse<IResponseApi<{ count: number; rows: IRegistroPortafolio[] }>>
  > {
    try {
      const respuesta = await this.http.get<
        IResponseApi<{ count: number; rows: IRegistroPortafolio[] }>
      >(`/portafolio/${cocoId}/search`, { params })
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async detailPortafolio(
    cocoId: number,
    cocsId: number
  ): Promise<AxiosResponse<IResponseApi<IRegistroPortafolio>>> {
    try {
      const respuesta = await this.http.get<IResponseApi<IRegistroPortafolio>>(
        `/portafolio/${cocoId}/detail/${cocsId}`
      )
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async savePortafolio(
    cocoId: number,
    tipo: PortafolioItemType,
    registros: IRegistroPortafolio[]
  ): Promise<AxiosResponse<IResponseApi<IGuardadoPortafolio>>> {
    try {
      const respuesta = await this.http.post<IResponseApi<IGuardadoPortafolio>>(
        `/portafolio/${cocoId}/save`,
        { tipo, registros }
      )
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async deletePortafolio(
    cocoId: number,
    tipo: PortafolioItemType,
    ids: number[]
  ): Promise<
    AxiosResponse<
      IResponseApi<{ eliminados: number; resumen: ResumenPortafolio }>
    >
  > {
    try {
      const respuesta = await this.http.post<
        IResponseApi<{ eliminados: number; resumen: ResumenPortafolio }>
      >(`/portafolio/${cocoId}/delete`, { tipo, ids })
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async previewExcel(
    cocoId: number,
    archivo: FormData
  ): Promise<
    AxiosResponse<
      IResponseApi<{
        errores: string[]
        resultados: Record<string, number>
        altas: number
        modificaciones: number
      }>
    >
  > {
    try {
      const respuesta = await this.http.post<
        IResponseApi<{
          errores: string[]
          resultados: Record<string, number>
          altas: number
          modificaciones: number
        }>
      >(`/portafolio/${cocoId}/preview-excel`, archivo)
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
  async estadoExcel(
    cocoId: number,
    uuid: string
  ): Promise<
    AxiosResponse<
      IResponseApi<{
        uuid: string
        cocoId: number
        etapa: string
        errores: string[]
      }>
    >
  > {
    try {
      const respuesta = await this.http.get<
        IResponseApi<{
          uuid: string
          cocoId: number
          etapa: string
          errores: string[]
        }>
      >(`/portafolio/${cocoId}/estado-excel`, { params: { uuid } })
      return respuesta
    } catch (error) {
      this.errorHandle(error as import('axios').AxiosError)
      throw error
    }
  }
}
